import React, { useState } from 'react';
import { timeAgo } from '../utils/time';
import Icon from './Icon';
import { DOCUMENT_KIND_LABELS, DOCUMENT_STATUS_LABELS } from './documentUtils';
import './AdminPanel.css';

// The date field each status stands for, so the person can correct it before confirming
const STATUS_DATE = {
  sent: 'sent_at', received: 'received_at', signed_by_partner: 'partner_signed_at',
  signed_by_tileward: 'tileward_signed_at', fully_signed: 'fully_signed_at'
};

/**
 * One thing the legal drive or the eSignature audit log noticed, for a person to decide on, shaped like
 * FindingCard: what changed, which record it looks like, Confirm and Not this. Confirm opens kind, status, title
 * and date to adjust first. A flagged one (a decline) has nothing to apply and can only be seen and dismissed.
 * onConfirm(suggestion, fields) and onDismiss(suggestion) return promises; a refusal's message is shown here.
 */
const DocumentSuggestionCard = ({ suggestion, onConfirm, onDismiss, busy = false }) => {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    kind: suggestion.suggested_kind || 'other',
    status: suggestion.suggested_status || 'sent',
    title: suggestion.document ? suggestion.document.title : (suggestion.file_name || ''),
    date: (suggestion.event_at || '').slice(0, 10)
  });
  const document = suggestion.document;
  const id = (field) => `dsug-${suggestion.id}-${field}`;
  const set = (field) => (event) => setForm(current => ({ ...current, [field]: event.target.value }));

  const run = async (action) => {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err.message);
    }
  };

  const confirm = () => run(() => {
    const fields = { kind: form.kind, status: form.status };
    if (!document && form.title.trim()) fields.title = form.title.trim();
    if (document && form.kind === document.kind) delete fields.kind;
    if (form.date && STATUS_DATE[form.status]) fields[STATUS_DATE[form.status]] = form.date;
    return onConfirm(suggestion, fields);
  });

  const headline = suggestion.flagged
    ? 'Needs a look: a signer declined'
    : document
      ? `${suggestion.suggested_status_label} — ${document.title}`
      : 'Add as a document?';

  return (
    <li className="adm-event adm-finding dsug">
      <div className="adm-event__line">
        <span className="adm-event__who">{headline}</span>
        <span className="adm-event__when">{timeAgo(suggestion.event_at || suggestion.created_at)}</span>
      </div>
      <div className="adm-event__summary">{suggestion.reason}</div>
      <div className="adm-finding__meta">
        {document
          ? <>Looks like your {document.kind_label || DOCUMENT_KIND_LABELS[document.kind]} “{document.title}”, now {document.status_label.toLowerCase()}.</>
          : <>No record on file for this yet{suggestion.suggested_kind ? `; it looks like ${(DOCUMENT_KIND_LABELS[suggestion.suggested_kind] || 'a document')}` : ''}.</>}
        {suggestion.file_name && !document && <> File: {suggestion.file_name}.</>}
        {' '}<a className="adm-source-link" href={suggestion.drive_url} target="_blank" rel="noopener noreferrer">Open in Drive</a>
      </div>

      {editing && !suggestion.flagged && (
        <div className="dsug__edit">
          <label className="cdoc-field" htmlFor={id('kind')}>
            <span>Kind</span>
            <select id={id('kind')} className="rm-input" value={form.kind} onChange={set('kind')} disabled={busy}>
              {Object.entries(DOCUMENT_KIND_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </label>
          <label className="cdoc-field" htmlFor={id('status')}>
            <span>Status</span>
            <select id={id('status')} className="rm-input" value={form.status} onChange={set('status')} disabled={busy}>
              {Object.entries(DOCUMENT_STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </label>
          {!document && (
            <label className="cdoc-field" htmlFor={id('title')}>
              <span>Title</span>
              <input id={id('title')} className="rm-input" value={form.title} onChange={set('title')} maxLength={200} disabled={busy} />
            </label>
          )}
          {STATUS_DATE[form.status] && (
            <label className="cdoc-field" htmlFor={id('date')}>
              <span>Date</span>
              <input id={id('date')} className="rm-input" type="date" value={form.date} onChange={set('date')} disabled={busy} />
            </label>
          )}
        </div>
      )}

      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}

      <div className="adm-finding__actions">
        {!suggestion.flagged && !editing && (
          <button type="button" className="rm-button rm-button--primary" disabled={busy} onClick={() => setEditing(true)}>
            <Icon name="check" /> Confirm
          </button>
        )}
        {!suggestion.flagged && editing && (
          <>
            <button type="button" className="rm-button rm-button--primary" disabled={busy || (!document && !form.title.trim())} onClick={confirm}>
              <Icon name="check" /> {busy ? 'Saving...' : 'Confirm'}
            </button>
            <button type="button" className="rm-button" disabled={busy} onClick={() => setEditing(false)}>Back</button>
          </>
        )}
        <button type="button" className="rm-button rm-button--quiet" disabled={busy} onClick={() => run(() => onDismiss(suggestion))}>
          {suggestion.flagged ? 'Seen' : 'Not this'}
        </button>
      </div>
    </li>
  );
};

export default DocumentSuggestionCard;
